import { PACKAGE_INDICATORS } from "@directv-digital/dds.services.channel-lineup-provider";
import { logError } from "./logger";
import { IDtvButtonProps } from "@directv-digital/dds.ui.dtv-button";
import { isExcludedFastChannel } from "./channels-service-helpers";

const PARSER_NAME = "ChannelLineupParser";

export const parseChannelsData = (
  chnlSrc: string,
  channels: any[],
  packagesIds: string[],
  // packageCategories: KeyValuePair[],
  packages: any[],
  // myFreeDTVConfig: any,
  genrePackages: any,
  isSatelliteChlup?: any
): any[] => {
  try {
    const FALLBACK_CHNL_ICON_URL = 'https://www.directv.com/dtvassets/global/channel-icons/m-n/Channel_Icon_No-Logo.svg'
    const parseData = [] as any[];
    let uuidIx = 0;

    if (channels && Array.isArray(channels)) {
      // eslint-disable-next-line no-param-reassign
      packagesIds = packagesIds || [];

      // Satellite context: derived from the explicit flag or the satellite
      // channel source. FAST channels are app/online-only and not receivable on
      // satellite, so they are excluded from the satellite lineup + its counts
      // (WAT-10783). Streaming context is unchanged.
      const isSatelliteContext = Boolean(isSatelliteChlup) || chnlSrc === "satelliteclup";

      for (const c of channels) {
        // Skip FAST channels in satellite context (display-layer only).
        if (isExcludedFastChannel(c, isSatelliteContext)) {
          continue;
        }
        let packageList = c?.packageListSystem;
        if (packageList?.length && c?.excludedPackageList?.length) {
          packageList = packageList.filter((packge: string) => !c.excludedPackageList.includes(packge));
        }

        /** Build package indicators */
        let isInclAny = false;
        let isAddOnPromoAny = false;
        const indicators: PACKAGE_INDICATORS[] = new Array(packages.length).fill(null);
        for (const packag of packages) {
          const pkgArr = packag.packageId.split('TV_');
          const pkgId = pkgArr[pkgArr.length - 1];
          const pkgIdToCompare = isSatelliteChlup ? `DTV_${pkgId}` : `ATT_TV_${pkgId}`;
          if (c?.packageListSystem && c?.packageListSystem.includes(pkgIdToCompare)) {
            if (packagesIds.includes(packag.packageId)) {
              isInclAny = true;
            }
            indicators[packag.packageIndicatorIndex] = PACKAGE_INDICATORS.IN;
          } else if (c?.promoPackageList && c?.promoPackageList.includes(pkgIdToCompare)) {
            if (packagesIds.includes(packag.packageId)) {
              isAddOnPromoAny = true;
            }
            indicators[packag.packageIndicatorIndex] = PACKAGE_INDICATORS.PROMO;
          } else if (c?.addonPackageList && c?.addonPackageList.includes(pkgIdToCompare)) {
            if (packagesIds.includes(packag.packageId)) {
              isAddOnPromoAny = true;
            }
            indicators[packag.packageIndicatorIndex] = PACKAGE_INDICATORS.ADDON;
          }
        }

        const combinedChannelPkgs = [
          ...(c?.packageListSystem && Array.isArray(c.packageListSystem) ? c.packageListSystem : []),
          ...(c?.addonPackageList && Array.isArray(c.addonPackageList) ? c.addonPackageList : []),
          ...(c?.promoPackageList && Array.isArray(c.promoPackageList) ? c.promoPackageList : [])
        ];
        // Remove duplicates from combineChannelPkgs
        const seenPkgs = new Set<string>();
        let chnlPackages = combinedChannelPkgs.filter(pkg => {
          if (seenPkgs.has(pkg)) {
            return false;
          }
          seenPkgs.add(pkg);
          return true;
        });

        const enabledGenrePackages = genrePackages?.map(pkg => pkg.packageId);

        const genreIndicators: PACKAGE_INDICATORS[] = !isSatelliteChlup ? new Array(genrePackages?.length).fill(null) : [];
        if (!isSatelliteChlup) {
          // Resolve genre-pack membership by the most stable key available. Priority:
          //   1) billingProductCode — Eng/Mongo -> Epoch product code, never rotates:
          //      THE canonical join once the feed + package-config carry it.
          //   2) epochPackageName  — the feed's display name, authored in the catalog;
          //      covers the interim and the combo packs the snake alias can't match.
          //   3) ATT_TV_<snake>    — original fallback for single-word packs.
          const inAnyList = (key: string | undefined): boolean => Boolean(key) && Boolean(
            c?.packageListSystem?.includes(key as string) ||
            c?.addonPackageList?.includes(key as string) ||
            c?.promoPackageList?.includes(key as string)
          );
          // Canonical genre ids this channel resolved into (e.g. ATT_TV_MYNEWS_LOCALS).
          // Backfilled into chnlPackages below so the client render can bucket it.
          const resolvedGenreIds: string[] = [];
          for (const packag of genrePackages) {
            const pkgArr = packag.packageId.split('TV_');
            const pkgId = pkgArr[pkgArr.length - 1];
            const cfg = packag?.packageConfig;
            if (
              // Canonical join — SAFETY NET (defence in depth). The channels-service
              // adapter now resolves each feed PackageRef's billingProductCode to its
              // catalog id (buildBillingProductCodeMap) and emits it into
              // packageListSystem, so ATT_TV_<categoryKey> is present natively for
              // combo/renamed packs (verified via isolation: combo packs resolve on the
              // ATT_TV_<categoryKey> arm alone). This 3-arm chain is kept because it
              // still resolves membership at page-adapt time (package-config always warm
              // here) should the adapter map be cold at populate, AND it covers the
              // overlay-only MyHomeTeam pack which has no feed billingProductCode.
              // Order: billingProductCode (stable) -> catalog epochPackageName ->
              // ATT_TV_<snake> alias.
              inAnyList(cfg?.billingProductCode) ||
              inAnyList(cfg?.epochPackageName) ||
              inAnyList(`ATT_TV_${pkgId}`)
            ) {
              genreIndicators[packag?.packageIndicatorIndex] = PACKAGE_INDICATORS.IN;
              resolvedGenreIds.push(packag.packageId);
            }
          }
          // Backfill the resolved canonical genre ids into the channel's package list.
          // The client render (chlupStore.getBannerChannels) buckets a channel into a
          // genre section by matching ATT_TV_<categoryKey> in chnlPackages — and
          // isGenreChannel/condition below gate emission the same way. Packs whose feed
          // alias differs from the catalog id (MyNews -> ATT_TV_MYNEWS vs id
          // ATT_TV_MYNEWS_LOCALS; "MySports Original" -> ATT_TV_MYSPORTS_ORIGINAL vs
          // ATT_TV_MYSPORTS) resolve via billingProductCode above but would never carry
          // the catalog id, so they'd be dropped from the render. This makes them carry
          // exactly what already-working single-word packs (MyKids) carry — no new shape.
          for (const id of resolvedGenreIds) {
            if (!chnlPackages.includes(id)) {
              chnlPackages.push(id);
            }
          }
        }

        const isStream = chnlPackages.some(pkg => pkg.includes("ATT_TV_") || pkg.includes("ATT_TV_MIESPANOL"));
        const isSatellite = chnlPackages.some(pkg => pkg.includes("DTV_"));
        const isMyFreeDTV = chnlPackages.some(pkg => pkg.includes("MYFREE_DTV"));

        // Stream and satellite both gate an "English signature" channel the same
        // way: the channel belongs to at least one of the resolved English package
        // ids. The two were byte-identical, so collapsed to one.
        const isEnglishSignatureChannel = chnlPackages?.some(pkg => {
          return packagesIds.includes(pkg);
        });
        const isGenreChannel = chnlPackages?.some(pkg => {
          return enabledGenrePackages?.includes(pkg);
        });
        const isEnglishIncludedChannel = c?.packageListSystem?.some(pkg => {
          return packagesIds.includes(pkg);
        });
        const isGenreIncludedChannel = c?.packageListSystem?.some(pkg => {
          return enabledGenrePackages?.includes(pkg);
        });
        const addOnPromoPackageList =  c?.addonPackageList?.concat(c?.promoPackageList);

        const isAddOnIntenetOnly =
          !(isEnglishIncludedChannel || isGenreIncludedChannel) &&
          addOnPromoPackageList?.some(pkg => packagesIds.includes(pkg));
        chnlPackages = chnlPackages?.filter?.(pkg => packagesIds.includes(pkg) || (enabledGenrePackages?.includes(pkg)));

        // const edspCategoryPackages =
        //   packages.filter((pack) => pack.edspPackage !== false && packageCategory.key === pack.categoryId);
        // const isInclEnglishStream = !c?.packageListSystem ? false :
        //   edspCategoryPackages?.some(pkg => {
        //     const packageId = pkg?.packageId.toUpperCase();
        //     if (includedChnlPackages?.length === 0) return false;
        //     return includedChnlPackages?.includes(`ATT_TV_${packageId}`)
        //   });
        // const isAddOnPromoAny = edspCategoryPackages?.some(pkg => {
        //   const packageId = pkg?.packageId?.toUpperCase();
        //   if (addonPromoChnlPackages?.length === 0) return false;
        //   return addonPromoChnlPackages?.includes(`ATT_TV_${packageId}`);
        // });
        const condition = chnlSrc !== "satelliteclup" ?
          ((isStream && isEnglishSignatureChannel) || isGenreChannel) :
          (isSatellite && isEnglishSignatureChannel);

        if(condition) {
          // Pass engineering categories through unchanged so bucketChannelsByCategory
          // can match them directly against the CMS config (no translation here).
          // Flag-OFF: AEM channels carry a single categoryId slug; wrap it so
          // downstream array checks work for both paths.
          const categories: string[] = c?.categories?.length
            ? c.categories
            : (c?.categoryId ? [String(c.categoryId)] : []);
          const chnl = {
            uuid: `${c?.channelNameLabel}-${c?.tunerPosition}-${uuidIx++}`,
            name: c?.channelNameLabel || "",
            category: categories[0] || "",
            categories,
            icon: c?.channelLogoPath ?
              c?.channelLogoPath?.includes("directv.com") ?
                c?.channelLogoPath :
                `https://www.directv.com${c?.channelLogoPath?.startsWith("/") ? "" : "/"}${c?.channelLogoPath}` :
              FALLBACK_CHNL_ICON_URL,
            chnlNum: `${c?.tunerPosition || ""}`,
            indicators,
            genreIndicators,
            isInclAny,
            isAddOnPromoAny,
            chnlPackages: chnlPackages || [],
            // includedChnlPackages: includedChnlPackages || [],
            // addonPromoChnlPackages: addonPromoChnlPackages || [],
            isStream,
            isSatellite,
            isMyFreeDTV,
            isAddOnIntenetOnly
          };

          parseData.push(chnl);
        }
      }
      return parseData;
    }
    return [];
  } catch(error){
    logError(PARSER_NAME, error, { step: "parseChannelsData" });
    return [];
  }
};

export const parsePackagesData = (packagesData: any[]) => {
  try {
    const getCtaData = (cta: any): IDtvButtonProps => ({
      targeturl: cta.ctaUrl,
      label: cta?.cta,
      variant: cta?.variant || "uxprimary",
      mode: cta?.mode || "dark"
    })

    const packageBgColors = {
      ATT_TV_ENTERTAINMENT: "#F0FBFF",
      ATT_TV_CHOICE: "#FFFAF6",
      ATT_TV_ULTIMATE: "#E9F1FB",
      ATT_TV_PREMIER: "#FEF2FE",
      DTV_OPTIMO_MAS: "#E0F2FA"
    };

    const parsedPackages: any[] = [];

    for (const p of packagesData) {
      if (p?.packageId && (p?.categoryId || p?.categoryIdSystem)) {
        const pk = {
          id: p?.packageId,
          category: p?.categoryId || p?.categoryIdSystem,
          enabled: p?.enabled || false,
          name: p?.packageNameLabel || p?.tempPackageNameLabel || p?.packageNameLabelSystem,
          billingCodeLabel: p?.billingCodeLabel || "",
          packageIndicatorIndex: p?.packageIndicatorIndex,
          pkgBgColor: packageBgColors?.[p?.packageId] || p?.pkgBgcolor || "",
          pkgTextColor: '#102641', // p.pkgTextcolor || "",
          icon: p?.icon || "",
          googleAnalytics: p?.googleAnalytics,
          numOfChannels: p?.numOfChannels || "",
          packageConfig: {
            ...p?.packageConfig?.packageConfig,
            displayLabel: (p?.packageConfig?.displayLabel || p?.packageNameLabel || "")?.replace?.('™', '<sup> TM</sup>'),
            order: Number(p?.packageConfig?.order) || null,
            queryparam: p?.packageConfig?.queryparam || "",
            channelCount: (p?.packageConfig?.displayCount && p?.packageConfig?.signLetter && p?.packageConfig?.label) ?
              `${p?.packageConfig?.displayCount}${p?.packageConfig?.signLetter} ${p?.packageConfig?.label}` :
              p?.numOfChannels || "",
            price: p?.packageConfig?.price || "",
            isGenrePack: p?.isGenrePack,
            isGenreMiniPack: p?.isGenreMiniPack
          },
          ...p?.tooltip?.content && { tooltip: p?.tooltip },
          // ...p?.cta && { ctaProps: getCtaData(p.cta) },
          ...p?.ufCTA && { ctaProps: getCtaData(p?.ufCTA) },
          ...p?.gpCta && { gpCtaProps: getCtaData(p?.gpCta) }
        }

        parsedPackages.push(pk)
        // legacy
        // if (p?.cta) pk.cta = getCtaData(p.cta);
        // if (p?.gpCta) pk.gpCta = getCtaData(p.gpCta);
        // if (p?.ufCTA) pk.ufCTA = getCtaData(p?.ufCTA);
        // if (p?.salesCTA) pk.salesCTA = getCtaData(p?.salesCTA);
        // if (p?.internetCTA) pk.internetCTA = getCtaData(p?.internetCTA);
        // if (p?.satelliteCTA) pk.satelliteCTA = getCtaData(p?.satelliteCTA);
        // if (p?.supportInternetCTA) pk.supportInternetCTA = getCtaData(p?.supportInternetCTA);
        // if (p?.supportSatelliteCTA) pk.supportSatelliteCTA = getCtaData(p?.supportSatelliteCTA);
        // if (p?.servicesInternetCTA) pk.servicesInternetCTA = getCtaData(p?.servicesInternetCTA);
        // if (p?.servicesSatelliteCTA) pk.servicesSatelliteCTA = getCtaData(p?.servicesSatelliteCTA);
      }
    }
    return parsedPackages;
  } catch (error) {
    logError("ChannelLineupParser", error, { method: "parsePackagesData" });
  }
}
